import {createContext, useEffect, useReducer} from 'react';
import PropTypes from 'prop-types';

const wsStatus = {
  disconnected: 'DISCONNECTED',
  disconnecting: 'DISCONNECTING',
  connected: 'CONNECTED',
  connecting: 'CONNECTING',
}

const defaultWSState = {
  fullName: null,
  connectionId: null,
  eventId: null,
  status: wsStatus.disconnected,
  ws: null,
  msgs: {},
  users: [],
  isHost: null,
  hostId: null,
  showInvitation: false,
};

const WSState = createContext(defaultWSState);
const {Provider} = WSState;

const token = process.env.REACT_APP_WS_TOKEN;
const wsUrl = process.env.REACT_APP_WS_URL;

const MSG_TYPE = {
  userJoint: 'USER_JOINT',
  connected: 'CONNECTED',
  userLeft: 'USER_LEFT',
  userMsg: 'USER_MSG',
  userMsgSent: 'USER_MSG_SENT',
  getCount: 'GET_COUNT',
  sendInvite: 'SEND_INVITE',
  inviteReceived: 'INVITE_RECEIVED',
  sendStatusUpdate: 'SEND_STATUS_UPDATE',
  statusUpdateReceived: 'STATUS_UPDATE_RECEIVED',
};

const getMsgId = ({fullName, msg}) => {
  const id = Math.ceil(Math.random() * Math.pow(10, 5))
  return Buffer.from(
    `${fullName}-${msg}-${id}`
  ).toString('base64');
}

const wsReducer = (state, action) => {
  switch (action.type) {
    /**
     * WS CONNECTION ACTIONS
     * These are connection related actions
     */
    case 'connect':
      return {
        ...state,
        fullName: action.fullName,
        status: wsStatus.connecting,
        ws: new WebSocket([
          wsUrl,
          '?fullName=',
          action.fullName,
          '&eventId=',
          state.eventId,
          '&userType=',
          action.fullName === 'Host' ? 'HOST' : 'CUST',
          '&Auth=',
          token,
        ].join('')),
        isHost: action.fullName === 'Host' ? true : false,
      };
    case 'connected':
      state.ws.send(JSON.stringify({
        type: MSG_TYPE.getCount,
      }));

      return {
        ...state,
        status: wsStatus.connected,
      };
    case 'disconnect':
      state.ws.close();
      return {
        ...state,
        fullName: null,
        status: wsStatus.disconnecting,
        ws: null,
        msgs: {},
        users: [],
      };
    case 'disconnected':
      return {
        ...state,
        connectionId: null,
        status: wsStatus.disconnected,
        isHost: null,
      };

    /**
     * WS TRIGGER ACTIONS
     * These are actions that trigger WS events
     */

    case 'send-message':
      state.ws.send(JSON.stringify({
        msg: action.msg,
        type: MSG_TYPE.userMsg,
      }));

      return {
        ...state,
        msgs: {
          ...state.msgs,
          [getMsgId({fullName: state.fullName, msg: action.msg})]: {
            msg: action.msg,
            from: state.fullName,
            sentDate: new Date().toISOString(),
            sender: true,
          }
        }
      };
    case 'send-invitation':
      state.ws.send(JSON.stringify({
        type: MSG_TYPE.sendInvite,
        invitedConnectionId: action.connectionId,
      }));
      return {
        ...state,
      };
    case 'answer-invitation':
      if (action.isAccepted) {
        state.ws.send(JSON.stringify({
          type: MSG_TYPE.sendStatusUpdate,
          status: 'accepted',
          hostConnectionId: state.hostId,
        }));
      }

      return {
        ...state,
        showInvitation: false,
      };
    case 'send-audio-update':
      state.ws.send(JSON.stringify({
        type: MSG_TYPE.sendStatusUpdate,
        status: action.isMuted ? 'muted' : 'unmuted',
        hostConnectionId: state.hostId,
      }));

      return {
        ...state,
      };
    case 'host-end-call':
      state.users.forEach(user => user.inCall = false);
      
      return {
        ...state,
      };
    case 'user-end-call':
      state.ws.send(JSON.stringify({
        type: MSG_TYPE.sendStatusUpdate,
        status: 'call-ended',
        hostConnectionId: state.hostId,
      }));

      return {
        ...state,
        showInvitation: false,
      };
    
    /**
     * WS LISTENER ACTIONS
     * These are actions that were received via WS events
     */

    case MSG_TYPE.getCount:
      action.allUsers.map(user => {
        user.inCall = false;
        user.isMuted = false;
        if (user.type === 'HOST') state.hostId = user.connectionId;
      });

      return {
        ...state,
        connectionId: action.connectionId,
        users: action.allUsers,
        msgs: {
          ...state.msgs,
          [getMsgId({fullName: "system", msg: action.msg})]: {
            msg: action.msg,
            from: "system",
            sentDate: new Date().toISOString(),
          },
        },
      }
    case MSG_TYPE.userJoint:
      return {
        ...state,
        users: [...state.users, {...action.newcomer, inCall: false, isMuted: false}],
        hostId: action.newcomer.type === 'HOST' ? action.newcomer.connectionId : state.hostId,
        msgs: {
          ...state.msgs,
          [getMsgId({fullName: 'system', msg: action.msg})]: {
            msg: action.msg,
            from: 'system',
            sentDate: new Date().toISOString(),
          },
        },
      }
    case MSG_TYPE.userLeft:
      return {
        ...state,
        users: state.users.filter(user => user.connectionId !== action.userDisconnecting.connectionId),
        msgs: {
          ...state.msgs,
          [getMsgId({fullName: 'system', msg: action.msg})]: {
            msg: action.msg,
            from: 'system',
            sentDate: new Date().toISOString(),
          },
        },
      }
    case MSG_TYPE.userMsg:
      return {
        ...state,
        msgs: {
          ...state.msgs,
          [getMsgId({fullName: action.from, msg: action.msg})]: action,
        },
      };
    case MSG_TYPE.inviteReceived:
      return {
        ...state,
        showInvitation: true,
      }
    case MSG_TYPE.statusUpdateReceived:
      state.users.forEach(user => {
        if (user.connectionId === action.attendee.connectionId) {
          switch (action.status) {
            case 'accepted':
              user.inCall = true;
              break;
            case 'call-ended':
              user.inCall = false;
              user.isMuted = false;
              break;
            case 'muted':
              user.isMuted = true;
              break;
            case 'unmuted':
              user.isMuted = false;
              break;
            default:
              return;
          }
        }
      });

      return {
        ...state,
      };

    /**
     * DEFAULT
     */
    default:
      return state;
  }
}

const WSProvider = ({children, eventId}) => {
  const [state, dispatch] = useReducer(
    wsReducer,
    {...defaultWSState, eventId},
  );

  useEffect(() => {
    if (state && state.status === wsStatus.connecting && state.ws !== null) {
      state.ws.onopen = () => {
        console.log('WS opened');
        dispatch({type: 'connected'});
      }

      state.ws.onmessage = (e) => {
        const data = JSON.parse(e.data);

        if (data && data.type && Object.values(MSG_TYPE).indexOf(data.type) !== -1) {
          dispatch(data);
          return;
        }

        console.error('Failed to process received message', e);
      }

      state.ws.onclose = () => {
        console.log('WS closed');
        dispatch({type: 'disconnected'});
      }

      state.ws.onerror = (e) => {
        console.error('WS error', e);
      }
    }
  }, [state]);

  return (
    <Provider value={{state, dispatch}}>
      {children}
    </Provider>
  );
};

WSProvider.propTypes = {
  children: PropTypes.node,
  eventId: PropTypes.string.isRequired,
};

export {
  WSProvider,
  WSState,
};
